// src/api/proxyAuth.js
// Attaches the proxy REST API's bearer token to every call the dashboard makes
// to the proxy (:8001 / proxy-api.adlensfx.com).
//
// WHY A FETCH WRAPPER AND NOT AN IMPORT. Twenty-two modules build their own
// `PROXY_API_BASE` from REACT_APP_PROXY_API_URL and call fetch() directly
// (api/checks.js, api/launches.js, pages/ConfigPage.jsx's fourteen call sites,
// …). Threading a header through each one means ~40 edits where a single miss
// is an unexplained 401 in production and nothing catches it locally, because
// local dev leaves API_TOKEN unset and the proxy stays open. Wrapping fetch once
// covers every existing caller and every future one.
//
// REACT_APP_PROXY_API_TOKEN IS NOT A SECRET FROM ANYONE WHO WANTS IT.
// REACT_APP_* values are inlined at build time, so it ships inside the JS.
// Cloudflare Access on adlensfx.com does NOT protect it: Pages also serves the
// identical bundle from adtech-validator.pages.dev and from every
// per-deployment preview URL, and those hostnames have no Access app in front
// of them. Confirmed 2026-09-17 by fetching both tokens unauthenticated from
// the .pages.dev host.
//
// docs/proxy-credential-spec.md is the durable fix: refreshProxyCredential()
// below mints a short-lived, per-user JWT from the backend (after its own
// login) and the wrapper prefers it over PROXY_API_TOKEN. PROXY_API_TOKEN
// stays as the transition fallback — until the runbook §2.4 close-out, and for
// any environment where PROXY_JWT_SECRET isn't provisioned yet — so treat it
// as a speed bump, not authentication, until then. Do not widen what it can
// reach on that assumption.
//
// SCOPE. The wrapper attaches the header to a request only when its URL resolves
// under REACT_APP_PROXY_API_URL. Backend calls (api.adlensfx.com) carry their own
// JWT via authFetch.js and must not receive this one; same-origin and third-party
// URLs are left untouched so the token cannot leak to another host.

import { authHeader, handleAuthResponse } from './authFetch';

const PROXY_API_BASE = (process.env.REACT_APP_PROXY_API_URL || '').replace(/\/$/, '');
const PROXY_API_TOKEN = process.env.REACT_APP_PROXY_API_TOKEN || '';
const BACKEND_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const MINT_URL = `${BACKEND_BASE}/api/auth/proxy-credential`;

/** Resolve any fetch() input form (string | URL | Request) to an absolute URL string. */
function urlOf(input) {
  try {
    if (typeof input === 'string') return new URL(input, window.location.href).href;
    if (input instanceof URL) return input.href;
    if (input && typeof input.url === 'string') return new URL(input.url, window.location.href).href;
  } catch { /* unparseable input — treated as "not the proxy" below */ }
  return '';
}

/** True when this request is bound for the proxy REST API and should be authed. */
export function isProxyApiUrl(input) {
  if (!PROXY_API_BASE) return false;
  const href = urlOf(input);
  if (!href) return false;
  // Compare on the base + a boundary so `https://proxy-api.example.com` never
  // matches `https://proxy-api.example.com.evil.test/`.
  return href === PROXY_API_BASE || href.startsWith(`${PROXY_API_BASE}/`);
}

// ── Short-lived per-user proxy credential (docs/proxy-credential-spec.md) ──
//
// Cached in memory ONLY, never localStorage/state — the same rule proxyWs.js
// already follows for the WS token, because the value renders nowhere and a
// stored copy is a leak surface a bearer token in browser storage isn't worth.
let cachedCredential = null;   // { token, expiresAt } | null — expiresAt: ms epoch
let inFlightRefresh  = null;   // Promise | null — single-flight de-dup
let refreshTimer     = null;

/** Sync accessor — the fetch wrapper and proxyWs.js's withWsToken() both need
 *  the current token without awaiting anything. '' when nothing is cached or
 *  the cached token has already lapsed. */
export function getCachedProxyCredential() {
  if (!cachedCredential || Date.now() >= cachedCredential.expiresAt) return '';
  return cachedCredential.token;
}

// Proactively refresh at ~80% of the credential's lifetime so an active
// session never has to eat a 401-triggered round trip. unref() so a scheduled
// refresh never keeps a test process (or any non-browser host) alive.
function scheduleRefresh(expiresAt) {
  if (refreshTimer) clearTimeout(refreshTimer);
  const delay = Math.max((expiresAt - Date.now()) * 0.8, 0);
  refreshTimer = setTimeout(() => { refreshProxyCredential({ force: true }); }, delay);
  if (refreshTimer && typeof refreshTimer.unref === 'function') refreshTimer.unref();
}

/**
 * Mint (or reuse) a short-lived proxy credential from the backend. Single-
 * flight: concurrent callers share one in-flight request rather than each
 * minting their own. Never throws — every failure (not logged in yet, network
 * error, 503 because PROXY_JWT_SECRET isn't configured) resolves to `null` and
 * the caller falls back to PROXY_API_TOKEN / no auth, exactly the behavior
 * before this credential existed. A failed refresh leaves whatever was already
 * cached untouched — it is retried on next use, not on a timer.
 */
export function refreshProxyCredential({ force = false } = {}) {
  if (!force) {
    const cached = getCachedProxyCredential();
    if (cached) return Promise.resolve({ token: cached });
  }
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async () => {
    try {
      const resp = await fetch(MINT_URL, { headers: { ...authHeader() } });
      handleAuthResponse(resp);
      if (!resp.ok) return null;                    // not logged in / 503 not_configured / etc.
      const data = await resp.json().catch(() => null);
      if (!data || !data.ok || !data.token) return null;
      const ttlSeconds = Number(data.expiresIn) || 3600;
      cachedCredential = { token: data.token, expiresAt: Date.now() + ttlSeconds * 1000 };
      scheduleRefresh(cachedCredential.expiresAt);
      return { token: data.token, expiresIn: ttlSeconds };
    } catch {
      return null;                                    // network error — keep the old cached value, if any
    } finally {
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}

/**
 * Install the wrapper on `win.fetch`. Idempotent — a second call is a no-op, so
 * a hot reload cannot stack wrappers. Installs whenever a proxy base is
 * configured — unlike before, it no longer requires PROXY_API_TOKEN, because a
 * credential can now arrive at runtime via refreshProxyCredential() after this
 * module loads. No-op with no base configured, which keeps local dev (open
 * proxy, relative URLs) on the untouched native fetch.
 */
export function installProxyAuth(win = typeof window !== 'undefined' ? window : undefined) {
  if (!win || typeof win.fetch !== 'function') return false;
  if (!PROXY_API_BASE) return false;
  if (win.fetch.__proxyAuthInstalled) return false;

  const native = win.fetch.bind(win);

  // A Request passed as `input` already froze its own headers, so re-issue it
  // with the merged set rather than mutating in place.
  const dispatch = (input, init, headers) => {
    if (typeof Request !== 'undefined' && input instanceof Request) {
      return native(new Request(input, { headers }), init);
    }
    return native(input, { ...(init || {}), headers });
  };

  const wrapped = async (input, init) => {
    if (!isProxyApiUrl(input)) return native(input, init);

    const headers = new Headers((init && init.headers) || (input && input.headers) || undefined);
    // Never overwrite a caller's own Authorization — an explicit one wins, and
    // (since it isn't ours) we won't refresh-and-retry it on a 401 either.
    const callerSuppliedAuth = headers.has('Authorization');
    if (!callerSuppliedAuth) {
      // Precedence: the short-lived credential, then the transition-fallback
      // static token, then nothing (open proxy / unauthenticated).
      const bearer = getCachedProxyCredential() || PROXY_API_TOKEN;
      if (bearer) headers.set('Authorization', `Bearer ${bearer}`);
    }

    const resp = await dispatch(input, init, headers);

    // One refresh + retry on a 401 we could plausibly fix — a caller-supplied
    // Authorization is the caller's own problem, and a second 401 (post-
    // refresh) surfaces to the caller as-is rather than looping.
    if (resp.status === 401 && !callerSuppliedAuth) {
      const fresh = await refreshProxyCredential({ force: true });
      if (fresh && fresh.token) {
        const retryHeaders = new Headers(headers);
        retryHeaders.set('Authorization', `Bearer ${fresh.token}`);
        return dispatch(input, init, retryHeaders);
      }
    }
    return resp;
  };

  wrapped.__proxyAuthInstalled = true;
  win.fetch = wrapped;
  return true;
}

export { PROXY_API_BASE, PROXY_API_TOKEN };
